import React from "react";
import { motion } from "framer-motion";
import { Shield, MessageSquare } from "lucide-react";
import { SMS_HREF, reveal, getCurrentTier } from "./constants";
import { track } from "@/lib/analytics";

export function Services() {
  const tier = getCurrentTier();
  return (
    <section
      id="services"
      className="relative py-8 sm:py-12 bg-[#FFFFFF]"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div {...reveal} className="max-w-2xl mb-8">
          <div
            className="inline-flex items-stretch gap-0 rounded-sm overflow-hidden shadow-[0_8px_24px_rgba(0,0,0,0.18)] ring-1 ring-black/5"
            data-testid="military-discount-badge"
          >
            <div className="bg-[#5FBE1F] text-black px-6 py-4 grid place-items-center">
              <div className="font-display text-5xl sm:text-6xl leading-none tracking-normal">
                15%
              </div>
              <div className="font-display text-base sm:text-lg uppercase tracking-[0.18em] mt-2">
                Off
              </div>
            </div>
            <div className="bg-[#0A0A0A] text-white px-6 py-4 flex flex-col justify-center">
              <div className="flex items-center gap-2 text-[#5FBE1F]">
                <Shield className="w-4 h-4" />
                <span className="text-[11px] font-bold uppercase tracking-[0.22em]">
                  Thank you for your service
                </span>
              </div>
              <div className="font-display text-2xl sm:text-3xl mt-1 leading-tight">
                Military &amp; First<br className="sm:hidden" />
                <span className="sm:inline"> Responder Discount</span>
              </div>
              <div className="text-xs text-[#C9C9C9] mt-1">
                Active &amp; retired military, police, fire &amp; EMS · just mention it when you book
              </div>
            </div>
          </div>
        </motion.div>

        <div className="border-t-2 border-[#0A0A0A] mt-2" data-testid="services-menu">
          {/* Gutter Cleaning */}
          <div className="border-b border-dotted border-[#9CA09B]">
            <a
              href="#contact"
              className="group flex items-baseline gap-3 pt-5 pb-1 hover:bg-[#F5F5F5] transition-colors px-1"
              data-testid="service-cleaning-card"
            >
              <span className="font-display text-2xl sm:text-3xl text-[#000000] group-hover:text-[#5FBE1F] transition-colors">
                Gutter Cleaning
              </span>
              <span className="flex-1 border-b border-dotted border-[#9CA09B] mb-2" aria-hidden="true" />
              <span className="font-display text-2xl sm:text-3xl text-[#5FBE1F] whitespace-nowrap">
                {tier.menuPrice}
              </span>
            </a>
            <div className="text-xs text-[#4A4A46] px-1 pb-3">
              {tier.menuSubtext}
            </div>

            <div className="pl-6 sm:pl-10 pb-3" data-testid="neighbor-pricing">
              <a
                href="#contact"
                className="group flex items-baseline gap-3 py-2 border-b border-dotted border-[#E5E5E5] hover:bg-[#F5F5F5] transition-colors"
              >
                <span className="font-display text-base sm:text-lg text-[#000000]">
                  Neighbor Special
                </span>
                <span className="flex-1 border-b border-dotted border-[#E5E5E5] mb-1.5" aria-hidden="true" />
                <span className="font-display text-lg sm:text-xl text-[#5FBE1F] whitespace-nowrap">
                  2 for $150
                </span>
              </a>
              <div className="mt-2 text-xs text-[#4A4A46] leading-relaxed">
                Two homes on the same physical city block, cleaned the same day —
                $75 each instead of $100. Round up a neighbor and split the savings.
              </div>

              <a
                href="#contact"
                className="group flex items-baseline gap-3 py-2 mt-3 border-b border-dotted border-[#E5E5E5] hover:bg-[#F5F5F5] transition-colors"
                data-testid="detached-building-addon"
              >
                <span className="font-display text-base sm:text-lg text-[#000000]">
                  Detached Building Add-On
                </span>
                <span className="flex-1 border-b border-dotted border-[#E5E5E5] mb-1.5" aria-hidden="true" />
                <span className="font-display text-lg sm:text-xl text-[#5FBE1F] whitespace-nowrap">
                  +$40
                </span>
              </a>
              <div className="mt-2 text-xs text-[#4A4A46] leading-relaxed">
                Detached garage, shop, or shed cleaned the same visit — just $40
                added on top of your home cleaning. Easy to do while the ladder
                is already out.
              </div>
            </div>
          </div>

          {/* Gutter Covers */}
          <div className="border-b border-dotted border-[#9CA09B]">
            <a
              href="#contact"
              className="group flex items-baseline gap-3 pt-5 pb-1 hover:bg-[#F5F5F5] transition-colors px-1"
              data-testid="service-covers-card"
            >
              <span className="font-display text-2xl sm:text-3xl text-[#000000] group-hover:text-[#5FBE1F] transition-colors">
                Gutter Cover Installation
              </span>
              <span className="flex-1 border-b border-dotted border-[#9CA09B] mb-2" aria-hidden="true" />
              <span className="font-display text-xl sm:text-2xl text-[#0A0A0A] whitespace-nowrap">
                Custom Quote
              </span>
            </a>

            <div className="pl-6 sm:pl-10 pb-3">
              {[
                ["GutterRX", "Heavy-duty perforated metal cover"],
                ["Aqua Armor", "Fine-mesh micro-filter system"],
              ].map(([name, desc]) => (
                <div
                  key={name}
                  className="flex items-baseline gap-3 py-2 border-b border-dotted border-[#E5E5E5]"
                  data-testid={`cover-${name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <span className="font-display text-base sm:text-lg text-[#000000]">
                    {name}
                  </span>
                  <span className="flex-1 border-b border-dotted border-[#E5E5E5] mb-1.5" aria-hidden="true" />
                  <span className="text-xs sm:text-sm text-[#4A4A46] whitespace-nowrap">
                    {desc}
                  </span>
                </div>
              ))}
              <div className="mt-2 text-xs text-[#4A4A46] leading-relaxed">
                Samples of both on the truck — and I'll leave you a brochure to compare side-by-side before you decide.
              </div>
            </div>
          </div>

          {/* Gutter Repairs (custom quote) */}
          <div className="border-b border-dotted border-[#9CA09B]">
            <a
              href="#contact"
              className="group flex items-baseline gap-3 pt-5 pb-1 hover:bg-[#F5F5F5] transition-colors px-1"
              data-testid="service-repairs-card"
            >
              <span className="font-display text-2xl sm:text-3xl text-[#000000] group-hover:text-[#5FBE1F] transition-colors">
                Gutter Repairs
              </span>
              <span className="flex-1 border-b border-dotted border-[#9CA09B] mb-2" aria-hidden="true" />
              <span className="font-display text-2xl sm:text-3xl text-[#5FBE1F] whitespace-nowrap">
                Custom Quote
              </span>
            </a>
            <div className="text-xs text-[#4A4A46] px-1 pb-3 leading-relaxed">
              Loose hangers, pulled-away sections, broken end caps, sagging
              runs, downspout reattachment, and more. Priced by the amount and
              type of work — $100 minimum.
            </div>
          </div>

          {/* Gutter Reseal Service */}
          <div className="border-b border-dotted border-[#9CA09B]">
            <a
              href="#contact"
              className="group flex items-baseline gap-3 pt-5 pb-1 hover:bg-[#F5F5F5] transition-colors px-1"
              data-testid="service-reseal-card"
            >
              <span className="font-display text-2xl sm:text-3xl text-[#000000] group-hover:text-[#5FBE1F] transition-colors">
                Gutter Reseal Service
              </span>
              <span className="flex-1 border-b border-dotted border-[#9CA09B] mb-2" aria-hidden="true" />
              <span className="font-display text-2xl sm:text-3xl text-[#5FBE1F] whitespace-nowrap">
                $195
              </span>
            </a>
            <div className="text-xs text-[#4A4A46] px-1 pb-3 leading-relaxed">
              Full reseal: gutter cleaning, removal of old sealant, and fresh
              sealant applied to every outlet, corner, end cap, and seam. Stops
              drips at the source.
            </div>
          </div>

          {/* Window Screen Replacement — header + variations */}
          <div className="pt-5 px-1" data-testid="service-screens-card">
            <div className="flex items-baseline gap-3">
              <span className="font-display text-2xl sm:text-3xl text-[#000000]">
                Window Screen Replacement
              </span>
              <span className="flex-1 border-b border-dotted border-[#9CA09B] mb-2" aria-hidden="true" />
              <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#5FBE1F] whitespace-nowrap">
                48-hr turnaround
              </span>
            </div>

            <div className="mt-2 pl-6 sm:pl-10">
              {[
                ["Small (24\u2033 × 36\u2033)", "$35"],
                ["Medium (32\u2033 × 48\u2033)", "$45"],
                ["Large (36\u2033 × 60\u2033)", "$55"],
                ["Patio door screen", "$75"],
                ["Pet-resistant upgrade", "+$20 / screen"],
              ].map(([label, price]) => (
                <a
                  key={label}
                  href="#contact"
                  className="group flex items-baseline gap-3 py-2 border-b border-dotted border-[#E5E5E5] hover:bg-[#F5F5F5] transition-colors"
                >
                  <span className="text-sm sm:text-base text-[#4A4A46] group-hover:text-[#000000] transition-colors">
                    {label}
                  </span>
                  <span className="flex-1 border-b border-dotted border-[#E5E5E5] mb-1.5" aria-hidden="true" />
                  <span className="font-display text-lg sm:text-xl text-[#5FBE1F] whitespace-nowrap">
                    {price}
                  </span>
                </a>
              ))}
            </div>
            <div className="border-b-2 border-[#0A0A0A] mt-3" />
          </div>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
          <p className="text-sm text-[#4A4A46]">
            All prices in USD. Minimum service call may apply.
          </p>
          <a
            href={SMS_HREF}
            onClick={() => track("text_gutters_click", { location: "services" })}
            className="inline-flex items-center gap-2 bg-[#5FBE1F] text-black font-bold px-6 py-3 rounded-sm hover:bg-[#4A9818] transition-colors"
            data-testid="services-text-cta"
          >
            <MessageSquare className="w-4 h-4" />
            Text "Gutters" to book
          </a>
        </div>
      </div>
    </section>
  );
}
